import {v4 as uuidv4} from 'uuid';
import Dexie from 'dexie';
import {isEffectivelyOffline} from './connectivity';
import {offlineDb} from './db';
import {getActiveSession} from './offlineSessionManager';
import {requestDrain} from './syncScheduler';
import type {OutboxItem, OutboxItemStatus} from './types';

/** Outbox statuses that still count as unsynced work. */
export const PENDING_STATUSES: OutboxItemStatus[] = [
  'pending',
  'syncing',
  'failed',
];

export interface EnqueueApplyLocalContext {
  sessionId: string;
  clientSubmissionId: string;
}

export interface AddOutboxItemArgs {
  sessionId: string;
  kind: string;
  payload: unknown;
  clientSubmissionId?: string;
}

export interface EnqueueArgs {
  kind: string;
  payload: unknown;
  contextKey?: string;
  /** Runs inside the enqueue transaction so its snapshot/file writes roll back
   * atomically with the outbox item. */
  applyLocal?: (ctx: EnqueueApplyLocalContext) => Promise<void>;
}

const nextSeq = async (sessionId: string): Promise<number> => {
  const last = await offlineDb.outbox
    .where('[sessionId+seq]')
    .between([sessionId, Dexie.minKey], [sessionId, Dexie.maxKey])
    .last();
  return (last?.seq ?? 0) + 1;
};

/**
 * Appends one outbox row using the caller's open `rw` transaction — the seam
 * `applyLocal` uses to queue companion items atomically with their parent.
 */
export const addOutboxItem = async ({
  sessionId,
  kind,
  payload,
  clientSubmissionId = uuidv4(),
}: AddOutboxItemArgs): Promise<OutboxItem> => {
  const queued: OutboxItem = {
    id: uuidv4(),
    sessionId,
    seq: await nextSeq(sessionId),
    kind,
    payload,
    clientSubmissionId,
    status: 'pending',
    createdAt: new Date().toISOString(),
  };
  await offlineDb.outbox.add(queued);
  return queued;
};

/**
 * Queues a write for the active session (of `contextKey`, if given), running
 * `applyLocal` in the same transaction so local writes stay atomic.
 */
export const enqueue = async ({
  kind,
  payload,
  contextKey,
  applyLocal,
}: EnqueueArgs): Promise<OutboxItem> => {
  // Only widen the transaction to the tables applyLocal touches when present.
  const tables = applyLocal
    ? [offlineDb.outbox, offlineDb.sessions, offlineDb.snapshots, offlineDb.files]
    : [offlineDb.outbox, offlineDb.sessions];

  const item = await offlineDb.transaction('rw', tables, async () => {
    const session = await getActiveSession(contextKey);
    if (!session) {
      throw new Error(
        'Cannot enqueue an offline write without an active session.'
      );
    }

    // applyLocal runs first so companion rows it queues take lower seqs and
    // drain before the parent that depends on them.
    const clientSubmissionId = uuidv4();
    if (applyLocal) {
      await applyLocal({sessionId: session.id, clientSubmissionId});
    }

    return addOutboxItem({
      sessionId: session.id,
      kind,
      payload,
      clientSubmissionId,
    });
  });

  // Poke the scheduler so a write made while briefly online drains promptly.
  if (!isEffectivelyOffline()) requestDrain();

  return item;
};

/**
 * Builds a `findParent` locator for DependsOnFailedItemError: the sibling of
 * `kind` in this session whose payload satisfies `predicate`.
 */
export const findParentOutboxItem =
  (
    sessionId: string,
    kind: string,
    predicate: (item: OutboxItem) => boolean
  ): (() => Promise<OutboxItem | undefined>) =>
  async () => {
    const siblings = await offlineDb.outbox
      .where('sessionId')
      .equals(sessionId)
      .toArray();
    return siblings.find(
      sibling => sibling.kind === kind && predicate(sibling)
    );
  };

/** Field changes that put a failed item back in line for the next sync run. */
export const RETRY_RESET_FIELDS: Partial<OutboxItem> = {
  status: 'pending',
  error: undefined,
  failureKind: undefined,
  attemptCount: undefined,
  lastAttemptAt: undefined,
};

/** Resets a failed item back to pending so the next sync run retries it. */
export const retryItem = async (id: string): Promise<void> =>
  offlineDb.transaction('rw', offlineDb.outbox, async () => {
    const item = await offlineDb.outbox.get(id);
    if (!item || item.status !== 'failed') {
      throw new Error('Only failed items can be retried.');
    }
    await offlineDb.outbox.update(id, RETRY_RESET_FIELDS);
  });

/**
 * Deletes queued items whatever their status. Removing a syncing row is safe:
 * the sync manager's applyIfStillSyncing drops the result of a row that is gone.
 */
export const discardItems = async (ids: string[]): Promise<void> => {
  await offlineDb.transaction('rw', offlineDb.outbox, () =>
    offlineDb.outbox.bulkDelete(ids)
  );
};

/** Deletes a queued item — only pending/failed items may be removed. */
export const deleteItem = async (id: string): Promise<void> =>
  offlineDb.transaction('rw', offlineDb.outbox, async () => {
    const item = await offlineDb.outbox.get(id);
    if (!item || (item.status !== 'pending' && item.status !== 'failed')) {
      throw new Error('Only pending or failed items can be deleted.');
    }
    await offlineDb.outbox.delete(id);
  });
